// 用户相关接口
//
// 对应 Android MainJZApiUserLoader.kt + MainJZMineLoader.kt

import { apiGet, apiPost, http, setToken } from "./client";
import { getDeviceId } from "@/utils/device";
import type { ApiEnvelope } from "./types";

/** 游客快速注册响应 */
export interface GuestRegisterResult {
  id: number;
  username: string;
  password: string;
}

/** 账号密码登录响应（accountlogin 真实字段） */
export interface AccountLoginResult {
  id: number;
  username: string;
  /** 后端给的随机昵称，例如 "Morton_42172" */
  nickname?: string;
  /** 默认头像 URL */
  avatar?: string;
  mobile?: string;
  token: string;
  real_name?: string;
  id_card?: string;
  gender?: string;
  vip_type?: number;
  vip?: unknown;
  is_real_name?: number;
  teenager?: number;
}

export interface UserInfo {
  user_id: number;
  user_nickname: string;
  user_avatar: string;
  mobile?: string;
  coin_balance?: number;
  /** VIP 等级："0"=无 "1"=VIP "2"=SVIP（后端可能用 string 或 number） */
  vip?: string | number;
  /** VIP 到期时间字符串。null/空 表示已过期 */
  vip_validity_time?: string;
  /** 其余字段未在 demo 阶段全部映射，后续按需补全 */
  [key: string]: unknown;
}

export interface MineInfo {
  user_info: UserInfo;
  sign_in?: { is_signed: boolean; days: number; reward: number };
  menus?: Array<{ id: string; title: string; icon: string; route?: string }>;
  /** /home/my 返回的其余动态字段（公告 / banner / 入口配置等） */
  [key: string]: unknown;
}

/** 发短信验证码。Android 用 GET /user/sendyzm/{mobile}，也有 v2 POST 版本 */
export async function sendSmsCode(mobile: string): Promise<void> {
  await apiGet(`/api/v1/client/user/sendyzm/${encodeURIComponent(mobile)}`);
}

/** 短信验证码登录。
 *  对齐 Android MainJZApiUserLoader.kt:127-137:
 *    params.mobile = mobile
 *    params.code = code.toInt()   ← 字段名是 code, 且是整数(不是 yzm 字符串!)
 *  响应是扁平结构 {token, id, nickname, avatar, mobile, vip_type, ...}, 跟 accountlogin
 *  一样, 不是 {token, user_info}。 */
export async function loginByMobile(
  mobile: string,
  yzm: string,
): Promise<AccountLoginResult> {
  const data = await apiPost<AccountLoginResult>(
    "/api/v1/client/user/mobilelogin",
    { mobile, code: Number(yzm) },
  );
  if (data?.token) setToken(data.token);
  return data;
}

/** 后端用户信息存在两种字段命名：
 *   accountlogin / getuserinfobytoken: { id, nickname, avatar, mobile, vip, … }
 *   home/my (老接口) / 部分场景: { user_id, user_nickname, user_avatar, … }
 * UI 模板统一读 user_* 命名。这个 helper 把任一形状对齐成统一 UserInfo
 *（两套命名都填，避免 UI 哪个字段读不到就空。）*/
export function normalizeUser(raw: unknown): UserInfo | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  const id = (r.user_id ?? r.id) as number;
  const nickname = (r.user_nickname ?? r.nickname ?? r.username) as string;
  const avatar = (r.user_avatar ?? r.avatar) as string;
  return {
    ...r,
    user_id: id,
    user_nickname: nickname,
    user_avatar: avatar ?? "",
    mobile: r.mobile as string | undefined,
    vip: r.vip as string | number | undefined,
    vip_validity_time: r.vip_validity_time as string | undefined,
  } as UserInfo;
}

/** 用 token 拉用户信息（启动时校验登录态） */
export async function fetchSelfByToken(silent = false): Promise<UserInfo | null> {
  try {
    const raw = await apiGet<unknown>(
      "/api/v1/client/user/getuserinfobytoken",
      undefined,
      { silent },
    );
    return normalizeUser(raw);
  } catch {
    return null;
  }
}

/** "我的" 页面聚合接口 */
export async function fetchMine(): Promise<MineInfo> {
  return apiGet<MineInfo>("/api/v1/client/home/my");
}

export interface ModifyProfilePayload {
  user_nickname?: string;
  user_avatar?: string;
  gender?: string;
}

/** 修改昵称 / 头像 / 性别 */
export function modifyProfile(payload: ModifyProfilePayload) {
  return apiPost<{ user_info?: UserInfo }>(
    "/api/v1/client/user/modify",
    payload,
  );
}

/** 退出登录：仅清本地 token；后端如有失效接口可在这里加
 *  注意：不清 guest_username/guest_password（参照 Gamino TokenStorage.clear()），
 *  这样游客退出后下次能用凭证快速重新拿 token */
export function logout(): void {
  setToken("");
}

// ============ 游客模式 ============

/** 游客快速注册 —— POST 无 body，后端按 Device-Id 生成新账号
 *  注意：后端对 body 很挑——传 `{}` 或 `""` 都会 500，必须完全无 body。
 *  axios 默认会在 POST 时给空 body 加 application/json 头，可能也算"有 body"，
 *  这里直接走 http.request 显式禁掉 Content-Type，强制真正的空 POST。 */
export async function registerGuest(): Promise<GuestRegisterResult> {
  const resp = await http.request<ApiEnvelope<GuestRegisterResult>>({
    method: "POST",
    url: "/api/v1/client/user/regist/fast",
    // 显式去掉默认 Content-Type，避免 axios 序列化空 body
    headers: { "Content-Type": undefined } as never,
    // 显式覆盖 transformRequest，确保不发任何 body
    transformRequest: [(_data, _headers) => undefined],
  });
  const body = resp.data;
  if (body.code !== 0) {
    throw new Error(body.detail || body.msg || `regist/fast ${body.code}`);
  }
  return body.data;
}

/** 账号密码登录（游客登录链路第二步，也供未来用户名登录场景使用） */
export async function loginByAccount(
  username: string,
  password: string,
  silent = false,
): Promise<AccountLoginResult> {
  const data = await apiPost<AccountLoginResult>(
    "/api/v1/client/user/accountlogin",
    { username, password },
    { silent },
  );
  if (data?.token) setToken(data.token);
  return data;
}

// ============ 第三方登录（海外：Google / Facebook 等）============

/** 对齐 Android 联运 thirdLogin：POST /api/v1/client/login/thirdparty
 *  body: device_id, channel_code, app_id(三方应用 id), openid(三方用户), access_token, d3_type
 *  响应与 accountlogin 同形：{ token, id, nickname, avatar, vip_type, ... } */
export interface ThirdPartyLoginParams {
  /** 渠道码（海外联运固定 "1"，同 Android） */
  channelCode: string;
  /** 三方平台应用 id（Google = OAuth Web client id） */
  appId: string;
  /** 三方用户 openid（Google = sub） */
  openid: string;
  /** 三方 access token */
  accessToken: string;
  /** 登录方式：google / facebook / ... */
  d3Type: string;
  /** Suppress global error toasts during automatic login. */
  silent?: boolean;
}

/** One-time nonce issued by the backend before Telegram OIDC login. */
export interface TelegramLoginNonce {
  nonce: string;
  /** Remaining lifetime in seconds. */
  expires_in: number;
}

/**
 * Request a Telegram OIDC nonce bound to the persistent Device-Id.
 * The following thirdparty request uses the same getDeviceId() value.
 */
export async function requestTelegramLoginNonce(): Promise<TelegramLoginNonce> {
  return apiPost<TelegramLoginNonce>(
    "/api/v1/client/login/telegram/nonce",
    { device_id: getDeviceId() },
  );
}

export async function loginByThirdParty(
  p: ThirdPartyLoginParams,
): Promise<AccountLoginResult> {
  const loginBody = {
    device_id: getDeviceId(),
    channel_code: p.channelCode,
    app_id: p.appId,
    openid: p.openid,
    access_token: p.accessToken,
    d3_type: p.d3Type,
  };

  if (p.d3Type === "telegram_miniapp") {
    const sent = new URLSearchParams(loginBody.access_token);
    const currentInitData = (
      window as typeof window & {
        Telegram?: { WebApp?: { initData?: string } };
      }
    ).Telegram?.WebApp?.initData ?? "";
    const current = new URLSearchParams(currentInitData);

    // Deliberately log timestamps only. initData, hashes, signatures and the
    // backend-issued user token must never appear in browser diagnostics.
    const authTimes = {
      sent_auth_date: sent.get("auth_date"),
      current_auth_date: current.get("auth_date"),
      browser_now: Math.floor(Date.now() / 1000),
    };
    console.info(`[telegram-miniapp-auth-times] ${JSON.stringify(authTimes)}`);
  }

  const data = await apiPost<AccountLoginResult>(
    "/api/v1/client/login/thirdparty",
    loginBody,
    { silent: p.silent },
  );
  if (data?.token) setToken(data.token);
  return data;
}
